import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import { AssetName } from 'components/asset/asset';

export enum LogoVariant {
    BUMBLE_FULL_HORIZONTAL,
    BUMBLE,
    BUMBLE_PREMIUM_BOXED,
    BUMBLE_VERTICAL,
    BUMBLE_BOOST,
    BUMBLE_COINS,
    BUMBLE_HIVE,
    MODE_DATE,
    MODE_BFF,
    MODE_BIZZ,
    PILL_MODE_BFF,
    PILL_MODE_BIZZ,
    BOOST,
    SPOTLIGHT,
    PREMIUM,
}

export enum LogoColor {
    PRIMARY,
    INHERITED,
}

export const mapLogoVariantToAssetName: Record<LogoVariant, AssetName> = {
    [LogoVariant.BUMBLE_FULL_HORIZONTAL]: 'logo-bumble-full-horizontal',
    [LogoVariant.BUMBLE]: 'logo-bumble',
    [LogoVariant.BUMBLE_PREMIUM_BOXED]: 'logo-bumble-premium-boxed',
    [LogoVariant.BUMBLE_VERTICAL]: 'logo-bumble-full-vertical',
    [LogoVariant.BUMBLE_BOOST]: 'logo-bumble-boost',
    [LogoVariant.BUMBLE_COINS]: 'logo-bumble-coins',
    [LogoVariant.BUMBLE_HIVE]: 'logo-bumble-hive',
    [LogoVariant.MODE_DATE]: 'logo-mode-date',
    [LogoVariant.MODE_BFF]: 'logo-mode-bff',
    [LogoVariant.MODE_BIZZ]: 'logo-mode-bizz',
    [LogoVariant.PILL_MODE_BFF]: 'logo-pill-mode-bff',
    [LogoVariant.PILL_MODE_BIZZ]: 'logo-pill-mode-bizz',
    [LogoVariant.BOOST]: 'logo-boost',
    [LogoVariant.SPOTLIGHT]: 'logo-spotlight',
    [LogoVariant.PREMIUM]: 'logo-premium',
};

type ImageProps = {
    aspectRatio: number;
    defaultHeight?: number;
};

// TODO: after Cosmos provides aspect ratios (DO-1238) use them here directly
// also maybe we'll just remove defaultHeight
export const mapLogoVariantToAspectRatio: Record<LogoVariant, ImageProps> = {
    [LogoVariant.BUMBLE_FULL_HORIZONTAL]: {
        aspectRatio: 171 / 30,
        defaultHeight: 30,
    },
    [LogoVariant.BUMBLE]: {
        aspectRatio: 105 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BUMBLE_PREMIUM_BOXED]: {
        aspectRatio: 160 / 29,
        defaultHeight: 29,
    },
    [LogoVariant.BUMBLE_VERTICAL]: {
        aspectRatio: 132 / 96,
        defaultHeight: 96,
    },
    [LogoVariant.BUMBLE_BOOST]: {
        aspectRatio: 202 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BUMBLE_COINS]: {
        aspectRatio: 189 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BUMBLE_HIVE]: {
        aspectRatio: 34 / 30,
        defaultHeight: 30,
    },
    [LogoVariant.MODE_DATE]: {
        aspectRatio: 64 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.MODE_BFF]: {
        aspectRatio: 37 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.MODE_BIZZ]: {
        aspectRatio: 53 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.PILL_MODE_BFF]: {
        aspectRatio: 44 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.PILL_MODE_BIZZ]: {
        aspectRatio: 52 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BOOST]: {
        aspectRatio: 82 / 29,
        defaultHeight: 29,
    },
    [LogoVariant.SPOTLIGHT]: {
        aspectRatio: 131 / 29,
        defaultHeight: 29,
    },
    [LogoVariant.PREMIUM]: {
        aspectRatio: 160 / 29,
        defaultHeight: 29,
    },
};

const mapLogoColorToClassname: { [key in LogoColor]: string } = {
    [LogoColor.PRIMARY]: 'logo--color-primary',
    [LogoColor.INHERITED]: 'logo--color-inherited',
};

type LogoProps = {
    variant?: LogoVariant;
    color?: LogoColor;
    children?: never;
    width?: number;
    height?: number;
};

const Logo: FunctionComponent<LogoProps> = ({
    variant = LogoVariant.BUMBLE_FULL_HORIZONTAL,
    color = LogoColor.PRIMARY,
    width,
    height,
}) => {
    const classnames = classNames({
        logo: true,
        [mapLogoColorToClassname[color]]: true,
    });

    let calculatedHeight = height || mapLogoVariantToAspectRatio[variant].defaultHeight;
    let calculatedWidth = width;
    const aspectRatio = mapLogoVariantToAspectRatio[variant].aspectRatio;

    if (calculatedWidth && !calculatedHeight) {
        calculatedHeight = Math.round(calculatedWidth / aspectRatio);
    }

    if (calculatedHeight && !calculatedWidth) {
        calculatedWidth = Math.round(calculatedHeight * aspectRatio);
    }

    const style = calculatedWidth
        ? {
              ['--width' as string]: `${calculatedWidth}px`,
              ['--height' as string]: `${calculatedHeight}px`,
          }
        : undefined;

    return (
        <div
            className={classnames}
            style={style}
            data-qa-icon-name={mapLogoVariantToAssetName[variant]}
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 119 44">
                <g fill="#444647" clipPath="url(#a)">
                    <path d="m117.7 11.2-4.3-7.4a1.3 1.3 0 0 0-1.1-.6h-8.6a1.3 1.3 0 0 0-1.1.6l-4.3 7.4a1.2 1.2 0 0 0 0 1.3l4.3 7.3.4.5.7.2h8.6a1.3 1.3 0 0 0 1-.7l4.4-7.4a1.2 1.2 0 0 0 0-1.2ZM105.6 7h4.8c.3 0 .5.1.8.4a1 1 0 0 1-.8 1.8h-4.8a1 1 0 1 1 0-2.2Zm3.7 9.6h-2.7c-.3 0-.5 0-.7-.3a1 1 0 0 1 0-1.5c.2-.2.4-.3.7-.3h2.7c.3 0 .6.1.8.3a1 1 0 0 1-.8 1.8Zm3-3.7h-8.7a1 1 0 1 1 0-2.2h8.7c.3 0 .6.2.8.4a1 1 0 0 1-.8 1.8ZM10 7C8 7 6.2 8 5.3 9.6v-8a1.3 1.3 0 0 0-2.7 0v17.6l.1.5a1.3 1.3 0 0 0 .8.8l.5.1a1.3 1.3 0 0 0 1-.4 1.3 1.3 0 0 0 .3-1V18a5.4 5.4 0 0 0 4.7 2.6c3.4 0 6.4-2.8 6.4-6.8s-3-6.7-6.4-6.7Zm-.4 11.2a4.3 4.3 0 0 1-4.3-4.4 4.2 4.2 0 0 1 2.6-4c.5-.2 1-.4 1.6-.4 2.4 0 4.2 1.7 4.2 4.4s-1.8 4.4-4.2 4.4ZM63.6 7c-2 0-3.9 1-4.7 2.6v-8a1.3 1.3 0 0 0-2.7 0v18.1a1.3 1.3 0 0 0 .8.8l.5.1a1.3 1.3 0 0 0 1-.4 1.3 1.3 0 0 0 .4-1V18a5.4 5.4 0 0 0 4.7 2.6c3.3 0 6.3-2.8 6.3-6.8s-3-6.7-6.3-6.7ZM63 18.2a4.3 4.3 0 0 1-4.2-4.4 4.2 4.2 0 0 1 2.6-4c.5-.2 1-.4 1.6-.4 2.4 0 4.2 1.7 4.2 4.4s-1.8 4.4-4.2 4.4ZM29.2 7.4a1.4 1.4 0 0 0-1 .4l-.3.4v5.1c0 3.2-1.6 4.8-3.9 4.8-1.9 0-3-1.4-3-3.5V8.7a1.3 1.3 0 0 0-2.8 0v6.4c0 3.2 2 5.5 5.2 5.5a5 5 0 0 0 4.4-2.6v1.2l.1.5a1.4 1.4 0 0 0 1.3.9 1.3 1.3 0 0 0 1-.4 1.3 1.3 0 0 0 .3-1V8.7a1.3 1.3 0 0 0-1.3-1.3ZM48.9 7c-2.1 0-3.9 1-4.8 2.6A4.7 4.7 0 0 0 39.8 7a4.8 4.8 0 0 0-4 2.6v-.9a1.3 1.3 0 0 0-2.8 0v10.5l.1.5a1.4 1.4 0 0 0 1.3 1l.5-.2c.2 0 .3-.2.4-.3a1.3 1.3 0 0 0 .4-1V14c0-3 1.4-4.4 3.4-4.4 1.7 0 3 1.2 3 3v6.7a1.4 1.4 0 0 0 .3 1l.5.3.5.1a1.3 1.3 0 0 0 1-.4 1.3 1.3 0 0 0 .3-1V14c0-3 1.4-4.4 3.5-4.4 1.7 0 3 1.2 3 3l-.1 6.7v.5a1.4 1.4 0 0 0 1.3 1l.6-.2.4-.3a1.3 1.3 0 0 0 .4-1V12c0-3-2.2-5-4.9-5M78 18.9a1.2 1.2 0 0 0-1.4-1l-.5.1c-1 0-1.7-.6-1.7-1.5V1.2L74 .7c-.2 0-.3-.2-.5-.3H73a1.4 1.4 0 0 0-1.3 1.3v15a3.9 3.9 0 0 0 3.8 3.8c.5 0 1 0 1.6-.2A1.2 1.2 0 0 0 78 19Zm13.2-5.2C91 11.7 90 7 85 7a6.5 6.5 0 0 0-6.7 6.8c0 4 3 6.7 6.6 6.7 2.2 0 4-.8 5.3-2.4a1.2 1.2 0 0 0 .2-1l-.2-.4-.3-.3a1.2 1.2 0 0 0-.9-.2 1.3 1.3 0 0 0-.8.5c-.9 1.2-2.1 1.5-3.3 1.5-2 0-3.7-1.4-4-3.8h10a.7.7 0 0 0 .4-.5v-.2ZM81 12.5a3.9 3.9 0 0 1 4-3c1.7 0 3.2 1 3.6 3H81ZM32 34c-3 0-4.5 1.4-4.5 5.5v3.1c0 .7-.6 1.3-1.3 1.3-.8 0-1.3-.6-1.3-1.3v-10c0-.7.5-1.3 1.3-1.3.7 0 1.3.6 1.3 1.3V34a5.3 5.3 0 0 1 4.2-2.4c.8-.1 1.4.3 1.5 1 0 .6-.3 1.3-1.1 1.3Zm-8.8 3.3c0 4-3 6.6-6.8 6.6a6.6 6.6 0 0 1-6.8-6.6c0-3.9 3-6.6 6.8-6.6s6.8 2.7 6.8 6.6Zm-2.6 0a4.2 4.2 0 1 0-8.4 0c0 2.6 2 4.3 4.2 4.3 2.3 0 4.2-1.7 4.2-4.3ZM7.1 27.7c-1.1 0-2 .7-2 2.1v1.5h3a1.1 1.1 0 1 1 0 2.2h-3v9.1c0 .8-.6 1.3-1.4 1.3-.7 0-1.3-.5-1.3-1.3v-9.1h-1a1.1 1.1 0 1 1 0-2.2h1v-1.5c0-2.8 1.9-4.6 4.7-4.6l1 .1c.6.1 1 .8.8 1.5-.3 1-1 .8-1.8.9ZM99 30.4a6.5 6.5 0 0 0-6.4 6.8c0 4 3 6.7 6.3 6.7 2 0 4-1 4.7-2.5v1.2a1.3 1.3 0 0 0 .8 1.3h.5a1.4 1.4 0 0 0 1-.4 1.3 1.3 0 0 0 .4-1V25c0-.3-.2-.6-.4-.9a1.3 1.3 0 0 0-2.3 1v7.8a5.4 5.4 0 0 0-4.7-2.5Zm.4 11.1c-2.4 0-4.2-1.7-4.2-4.3 0-2.7 1.8-4.4 4.2-4.4a4.3 4.3 0 0 1 4.2 4.4c0 2.7-2 4.3-4.2 4.3ZM55.6 34c-3.1 0-4.5 1.4-4.5 5.5v3.1c0 .7-.6 1.3-1.4 1.3-.7 0-1.3-.6-1.3-1.3v-10c0-.7.6-1.3 1.3-1.3.8 0 1.4.6 1.4 1.3V34a5.3 5.3 0 0 1 4.2-2.4c.7-.1 1.4.3 1.5 1 0 .6-.3 1.3-1.2 1.3Zm6-6.4c0 1-.8 1.6-1.8 1.6s-1.8-.7-1.8-1.6c0-1 .8-1.8 1.8-1.8s1.8.8 1.8 1.8Zm-3.2 15V32.3c0-.8.6-1.4 1.4-1.4.7 0 1.4.7 1.4 1.4v10.3c0 .7-.6 1.3-1.4 1.3-.8 0-1.4-.6-1.4-1.3Zm51-2.7c.6-.1 1.2.1 1.5.6.6 1 1.6 1.3 2.9 1.3 1.5 0 2.5-.7 2.5-1.7 0-.8-.6-1.3-1.8-1.5l-2.7-.6c-2.2-.5-3.5-1.6-3.5-3.4 0-2.3 2-4 5-4 2.2 0 3.8.8 4.7 2 .5.8 0 1.8-.8 2-.5 0-1-.2-1.3-.6a3 3 0 0 0-2.6-1.3c-1.6 0-2.3.8-2.3 1.7 0 .7.4 1.1 1.5 1.4l3 .7c2.1.4 3.4 1.6 3.4 3.4 0 2.6-2.2 4-5.4 4-2 0-3.9-.6-4.9-2-.6-.7-.1-1.8.8-2ZM76.2 37c-.2-1.8-1.2-6.6-6.3-6.6a6.5 6.5 0 0 0-6.7 6.8c0 4 3 6.7 6.5 6.7 2.3 0 4.2-.8 5.4-2.4l.2-.4a1.2 1.2 0 0 0-.2-1l-.3-.3a1.2 1.2 0 0 0-.9-.2l-.4.1-.4.4c-.9 1.2-2.1 1.5-3.4 1.5-1.8 0-3.6-1.4-3.8-3.8h9.6a.7.7 0 0 0 .5-.2l.2-.3v-.2ZM66 36a3.9 3.9 0 0 1 4-3.2c1.7 0 3.2 1 3.6 3.2H66Zm-21.3-8.3c-1.1 0-2 .7-2 2.1v1.5h3a1.1 1.1 0 1 1 0 2.2h-3v9.1c0 .8-.7 1.3-1.4 1.3s-1.3-.5-1.3-1.3v-9.1h-1a1.1 1.1 0 1 1 0-2.2h1v-1.5c0-2.8 1.8-4.6 4.7-4.6l.9.1c.7.1 1.1.8 1 1.5-.4 1-1.2.8-1.9.9ZM79.6 44a1.4 1.4 0 0 0 1-.4l.2-.5.1-.5V38c0-3.2 1.5-4.7 3.9-4.7 1.8 0 3 1.4 3 3.5v5.8a1.3 1.3 0 0 0 2.7 0v-6.4c0-3.2-2-5.4-5.1-5.4-2 0-3.8 1-4.5 2.5v-1.7a1.4 1.4 0 0 0-1.3-.9c-.2 0-.4 0-.5.2-.2 0-.4.1-.5.3a1.3 1.3 0 0 0-.4 1v10.4a1.3 1.3 0 0 0 1.4 1.3Z" />
                </g>
                <defs>
                    <clipPath id="a">
                        <path fill="#fff" d="M0 0h119v44H0z" />
                    </clipPath>
                </defs>
            </svg>
        </div>
    );
};

export default Logo;
